import { Button, Input, Select, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useGetAllPermissionQuery,
  useUpdateBulkStatusPermissionMutation,
} from "../../../../features/administrative-setting/administrativeSetting";
import {
  EnhancedGroupPermissions,
  TGetAllPermissionParams,
} from "../../../../features/administrative-setting/administrativeSetting.type";
import { useGetAllProductQuery } from "../../../../features/portal/Products/productsApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import {
  addKeyInArray,
  selectSearchOption,
  selectStringOption,
} from "../../../../utils/utils";

export default function UserGroupPermission() {
  const navigate = useNavigate();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useTable<TGetAllPermissionParams>();
  const [statusId, setStatusId] = useState<number>();
  const { isLoading, isError, error, data } = useGetAllPermissionQuery({
    params: { ...filter, search: keyword },
  });
  useError(isError, error, "Fetch permission data fail");
  const {
    isLoading: isProductLoading,
    isError: isProductError,
    error: productError,
    data: getProduct,
  } = useGetAllProductQuery({
    params: {
      page: 1,
      limit: 10000,
    },
  });
  useError(isProductError, productError, "Fetch product data fail");
  const [
    updateBulkStatusPermission,
    {
      isLoading: isStatusLoading,
      isError: isStatusError,
      error: statusError,
      isSuccess: isStatusSuccess,
    },
  ] = useUpdateBulkStatusPermissionMutation();
  useError(isStatusError, statusError, "Status update fail");

  const columns: ColumnsType<EnhancedGroupPermissions> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: "Group Role Name",
      dataIndex: "groupName",
      render: (val) => val || "-",
    },
    {
      title: "Product",
      dataIndex: "project",
      render: (val) => {
        return <span className="text-[#1B4DFF]">{val?.name}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 116,
      render: (val: EnhancedGroupPermissions["status"], record) => (
        <div className="flex gap-x-1">
          <Switch
            checked={val === "active"}
            onChange={(checked) => {
              updateBulkStatusPermission({
                ids: [record?.id],
                status: checked ? "active" : "inactive",
              });
              setStatusId(record?.id);
            }}
            loading={statusId === record?.id && isStatusLoading}
            disabled={isStatusLoading}
          />
          <span>{capitalize(val)}</span>
        </div>
      ),
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      width: 150,
      render: (val: number) => {
        return (
          <div className="flex justify-center items-center gap-2">
            <Button
              className="action-button"
              size="small"
              icon={<EditIcon />}
              onClick={() => navigate(`update/${val}`)}
            />
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
              onClick={() => navigate(`view/${val}`)}
            />
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (isStatusSuccess) {
      toast("success", "Status updated successfully");
      setStatusId(undefined);
    }
  }, [isStatusSuccess]);

  return (
    <SidebarLayout title="User Access Permission">
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.enhancedGroupPermissions)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ width: 215 }}
              allowClear
              onChange={handleSearch}
            />

            <Select
              allowClear
              showSearch
              placeholder="All Product"
              loading={isProductLoading}
              filterOption={selectSearchOption}
              options={getProduct?.data?.data?.map((el) => ({
                label: el?.name,
                value: el?.id,
              }))}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter.productId}
              onChange={(val) => handleFilter("productId", val)}
              popupMatchSelectWidth={250}
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={selectStringOption(
                ["active", "inactive"],
                "string",
                true
              )}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter.status}
              onChange={(val) => handleFilter("status", val)}
            />

            <Link to="/admin/user-access-permission/user-group/create">
              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
              >
                Create a Group
              </Button>
            </Link>
          </>
        }
      />
    </SidebarLayout>
  );
}
